import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { recipesAPI } from '../services/api';

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = resolve;
    el.onerror = reject;
    document.head.appendChild(el);
  });

const RecipePrint = () => {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [qrReady, setQrReady] = useState(false);
  const printedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const fetchRecipe = async () => {
      try {
        const res = await recipesAPI.getAll();
        const list = Array.isArray(res?.data) ? res.data : [];
        const found = list.find(r => String(r.id) === String(id));
        if (!cancelled) setRecipe(found || null);
      } catch {
        if (!cancelled) setRecipe(null);
      }
    };
    fetchRecipe();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    const ensureQR = async () => {
      if (window.QRCode && typeof window.QRCode.toCanvas === 'function') {
        setQrReady(true);
        return;
      }
      try {
        await loadScript('https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js');
        setQrReady(true);
      } catch {
        setQrReady(false);
      }
    };
    ensureQR();
  }, []);

  useEffect(() => {
    if (!canvasRef.current && !imgRef.current) return;
    const targetUrl = (() => {
      try {
        const origin = window.location.origin;
        return `${origin}/recipes?recipeId=${encodeURIComponent(id)}`;
      } catch {
        return `/recipes?recipeId=${encodeURIComponent(id)}`;
      }
    })();
    const triggerPrintOnce = () => {
      if (printedRef.current) return;
      printedRef.current = true;
      setTimeout(() => { try { window.print(); } catch {} }, 150);
    };
    const tryCanvas = () => {
      try {
        if (canvasRef.current && window.QRCode?.toCanvas) {
          window.QRCode.toCanvas(canvasRef.current, targetUrl, { width: 256, margin: 1 }, (err) => {
            if (err) {
              tryDataUrl() || setFallbackImg();
            } else {
              triggerPrintOnce();
            }
          });
          return true;
        }
      } catch {}
      return false;
    };
    const tryDataUrl = () => {
      try {
        if (imgRef.current && window.QRCode?.toDataURL) {
          window.QRCode.toDataURL(targetUrl, { width: 256, margin: 1 }, (e, dataUrl) => {
            if (!e && dataUrl) {
              imgRef.current.src = dataUrl;
            } else {
              setFallbackImg();
            }
          });
          return true;
        }
      } catch {}
      return false;
    };
    const setFallbackImg = () => {
      try {
        if (imgRef.current) {
          const fallback = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(targetUrl)}&margin=1`;
          imgRef.current.src = fallback;
          imgRef.current.style.display = 'block';
          if (canvasRef.current) canvasRef.current.style.display = 'none';
          // print will be triggered on img load handler below
        }
      } catch {}
    };
    if (!tryCanvas()) {
      if (!tryDataUrl()) {
        setFallbackImg();
      }
    }
  }, [qrReady, id]);

  const handlePrint = () => {
    try { window.print(); } catch {}
  };

  return (
    <div className="print-root min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-md border rounded-lg p-6 shadow print:shadow-none">
        <h1 className="text-2xl font-semibold text-gray-900 text-center">
          {recipe ? (recipe.is_deleted ? `Deleted — ${recipe.name}` : recipe.name) : 'Recipe'}
        </h1>
        <p className="text-center text-sm text-gray-600 mt-1">Scan to open this recipe</p>
        <div className="flex items-center justify-center mt-4 space-x-0">
          <canvas ref={canvasRef} width={256} height={256} className="border" style={{ display: 'block' }} />
          <img ref={imgRef} alt="QR Code" className="border" style={{ width: 256, height: 256, display: 'none' }} onLoad={(e) => {
            // If img loads, hide canvas and show img
            try {
              if (e?.currentTarget) {
                e.currentTarget.style.display = 'block';
                if (canvasRef.current) canvasRef.current.style.display = 'none';
              }
            } catch {}
          }} />
        </div>
        <div className="mt-4 text-center text-xs text-gray-500 break-all">
          {(() => {
            try {
              const origin = window.location.origin;
              return `${origin}/recipes?recipeId=${encodeURIComponent(id)}`;
            } catch {
              return `/recipes?recipeId=${encodeURIComponent(id)}`;
            }
          })()}
        </div>
        <div className="mt-6 flex justify-center gap-3 no-print">
          {/* Auto-prints; no button needed */}
        </div>
      </div>
      <style>{`
        @media print {
          /* Only print our print-root content */
          body * { visibility: hidden !important; }
          .print-root, .print-root * { visibility: visible !important; }
          /* Remove layout padding/heights that can push to an extra page */
          .print-root { min-height: auto !important; padding: 0 !important; margin: 0 auto !important; }
          .print-root > div { box-shadow: none !important; }
          /* Hide any non-print controls */
          .no-print { display: none !important; }
          body, html { background: #fff !important; }
          @page { size: auto; margin: 0.5in; }
        }
      `}</style>
    </div>
  );
};

export default RecipePrint;


